import { TICKS_PER_BAR, TICKS_PER_BEAT } from '../av/time';
import type { EventCategory, ReactionType } from '../domain/schemas';
import type { Command, ParseError } from './commands';
import { NOUNS, NUMBER, parseTarget, toNumber, verbAt } from './motionGrammar';

/**
 * Deterministic audio-visual sync grammar (Phase 4A). A bounded set of phrasings becomes
 * typed `sync.*` commands; anything else falls through to the other grammars or is
 * rejected. Nothing is guessed: names are resolved against the session's scene when the
 * command runs, and ambiguous references are refused with a question.
 *
 * Accepted shapes (case-insensitive; numbers as digits or words):
 *  - "synchroni[sz]e|sync|link this animation with|to the <…> track|music|song|beat"
 *  - "<reaction> <target> on every|each [Nth|second|other] <event>"   pulse the neon sign on every kick
 *  - "make|have <target> <reaction> on every <event>"                  make the character bounce on every snare
 *  - "flash the background|screen on the first beat of each bar", "shake the camera on every 808"
 *  - "make <target> <motion> every N bars|beats [starting at bar M]"   make the character jump every four bars
 *  - "start the <motion> [animation|motion] at bar N [beat B]" / "… at beat N"
 *  - "increase|decrease|raise|lower|boost|reduce the <kind> strength [by N%]", "make the pulse stronger|weaker"
 *  - "remove|delete the [<event>] <kind|reaction>"                      remove the kick reaction
 *  - "loop bars N through|to M", "stop looping"
 *  - "move the <cue or marker> to bar N [beat B] | beat N"
 *  - "add a marker at bar N [beat B] | beat N [called <label>]"
 *  - "preview|play the synchronized|synced animation", "pause the sync preview"
 */
type Result = { command: Command } | { commands: Command[] } | { error: ParseError } | null;
type SyncTarget = Extract<Command, { type: 'sync.react' }>['target'];

const err = (segment: string, message: string, example: string): Result => ({ error: { kind: 'invalid-value', message, segment, suggestions: [example] } });

const EVENT_NOUNS: [RegExp, EventCategory][] = [
  [/^kick(?: drum)?s?$/, 'kick'],
  [/^snares?$/, 'snare'],
  [/^claps?$/, 'clap'],
  [/^(?:hi-?hats?|hihats?|hats?)$/, 'hats'],
  [/^(?:perc|percussion|percussion hits?)$/, 'perc'],
  [/^cowbells?$/, 'cowbell'],
  [/^(?:808s?|bass(?: notes?)?|808 notes?)$/, 'bass808'],
  [/^(?:lead|lead notes?|melody notes?)$/, 'lead'],
  [/^(?:pad|pad chords?|chords?)$/, 'pad'],
  [/^beats?$/, 'beat'],
  [/^(?:bars?|downbeats?)$/, 'bar'],
  [/^(?:sections?|section changes?)$/, 'section'],
  [/^(?:markers?|manual markers?)$/, 'manual'],
  [/^(?:clips?|clip starts?)$/, 'clip'],
];
export function eventCategory(noun: string): EventCategory | null {
  const n = noun.trim();
  for (const [re, c] of EVENT_NOUNS) if (re.test(n)) return c;
  return null;
}

const ORDINALS: Record<string, number> = { other: 2, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7, eighth: 8 };

/** "on every kick", "on each second snare", "on every 4th beat", "on the first beat of each bar", "on kicks and 808s". */
function parseEvents(phrase: string): { categories: EventCategory[]; every: number } | null {
  const p = phrase.trim();
  if (/^on (?:the )?(?:first beat|downbeat|one) of (?:each|every) bar$/.test(p) || /^on (?:each|every) (?:bar|downbeat)$/.test(p)) return { categories: ['bar'], every: 1 };
  const m = p.match(/^on (?:every|each|all(?: the)?|the) (?:(other|second|third|fourth|fifth|sixth|seventh|eighth|\d+(?:st|nd|rd|th)) )?(.+)$/) ?? p.match(/^on ()(.+)$/);
  if (!m) return null;
  const every = m[1] ? (ORDINALS[m[1]] ?? Number.parseInt(m[1], 10)) : 1;
  const nouns = m[2]!.split(/\s*(?:\+|\bor\b|&|\/)\s*/);
  const categories: EventCategory[] = [];
  for (const n of nouns) {
    const c = eventCategory(n);
    if (!c) return null;
    if (!categories.includes(c)) categories.push(c);
  }
  return categories.length && Number.isFinite(every) && every >= 1 ? { categories, every } : null;
}

/** Reaction verbs → reaction (and defaults). */
const REACTION_VERBS: [RegExp, ReactionType, Partial<{ direction: 'up' | 'down' | 'left' | 'right' }>][] = [
  [/^(?:pulse|pulses|pulsate|pulsates|throb|throbs|pump|pumps)$/, 'scale', {}],
  [/^(?:bounce|bounces|hop|hops)$/, 'position', { direction: 'up' }],
  [/^(?:drop|drops|dip|dips)$/, 'position', { direction: 'down' }],
  [/^(?:spin|spins|twist|twists|rotate|rotates|wobble|wobbles)$/, 'rotation', {}],
  [/^(?:fade|fades|dim|dims|flicker|flickers)$/, 'opacity', {}],
  [/^(?:glow|glows|light up|lights up|flash|flashes)$/, 'tint', {}],
  [/^(?:blink|blinks|toggle|toggles)$/, 'visibility', {}],
];

function reactionOf(verb: string): { reaction: ReactionType; direction?: 'up' | 'down' | 'left' | 'right' } | null {
  for (const [re, reaction, extra] of REACTION_VERBS) if (re.test(verb)) return { reaction, ...extra };
  return null;
}

function syncTarget(phrase: string): SyncTarget | null {
  const t = phrase.trim();
  if (/^(?:the |this )?(?:background|bg)$/.test(t) || /^(?:the |this )?(?:screen|scene|whole scene|camera|view|frame)$/.test(t)) return { kind: 'scene' };
  const sel = t.match(/^(?:the |this |that )?selected (.+)$/);
  if (sel && !/^(?:object|shape|item|character|hero|person|figure|selection)s?$/.test(sel[1]!)) return { kind: 'selected-name', name: sel[1]!.trim() };
  return parseTarget(t);
}

/** "bar 3", "bar 3 beat 2", "beat 16" → ticks (1-based positions). */
export function parsePosition(phrase: string): number | null {
  const m = phrase.trim().match(new RegExp(String.raw`^(?:the )?(?:bar ${NUMBER}(?:,? beat ${NUMBER})?|beat ${NUMBER})$`));
  if (!m) return null;
  if (m[3] !== undefined) {
    const beat = toNumber(m[3]);
    return Number.isInteger(beat) && beat >= 1 ? (beat - 1) * TICKS_PER_BEAT : null;
  }
  const bar = toNumber(m[1]!);
  const beat = m[2] !== undefined ? toNumber(m[2]) : 1;
  if (!Number.isInteger(bar) || bar < 1 || !Number.isInteger(beat) || beat < 1 || beat > 4) return null;
  return (bar - 1) * TICKS_PER_BAR + (beat - 1) * TICKS_PER_BEAT;
}

const POSITION = String.raw`((?:bar ${NUMBER}(?:,? beat ${NUMBER})?)|(?:beat ${NUMBER}))`;

export function parseSyncSegment(segment: string): Result {
  let m: RegExpMatchArray | null;

  // ---- Create a session.
  if (/^(?:synchroni[sz]e|sync|link|match) (?:this|the|my|our) (?:animation|scene|visuals?|video) (?:with|to) (?:the |this |my |our )?(?:[a-z0-9 -]{0,30} )?(?:track|music|song|beat|audio|arrangement)$/.test(segment)) {
    return { command: { type: 'sync.create' } };
  }

  // ---- Transport of the session.
  if (/^(?:preview|play|start|run) (?:the )?(?:synchroni[sz]ed|synced|sync(?:ed)?) ?(?:animation|preview|session|scene|video)?$/.test(segment) || /^preview the sync(?: session)?$/.test(segment)) return { command: { type: 'sync.play' } };
  if (/^(?:pause|stop) (?:the )?(?:synchroni[sz]ed|synced|sync) ?(?:animation|preview|session|scene|playback)?$/.test(segment)) return { command: { type: 'sync.pause' } };

  // ---- Loop.
  if ((m = segment.match(new RegExp(String.raw`^loop (?:from )?bars? ${NUMBER} (?:through|thru|to|until|-) (?:bar )?${NUMBER}$`)))) {
    const from = toNumber(m[1]!);
    const to = toNumber(m[2]!);
    if (!Number.isInteger(from) || !Number.isInteger(to) || from < 1 || to < from) return err(segment, 'Loop whole bars: “loop bars 1 through 8”.', 'loop bars 1 through 8');
    return { command: { type: 'sync.loop', fromBar: from, toBar: to } };
  }
  if ((m = segment.match(new RegExp(String.raw`^loop the first ${NUMBER} bars$`)))) {
    const to = toNumber(m[1]!);
    if (!Number.isInteger(to) || to < 1) return err(segment, 'Say how many bars.', 'loop the first 8 bars');
    return { command: { type: 'sync.loop', fromBar: 1, toBar: to } };
  }
  if (/^(?:stop looping|turn off the loop|disable the loop|no loop|unloop)$/.test(segment)) return { command: { type: 'sync.loopOff' } };

  // ---- Strength.
  if ((m = segment.match(/^(increase|raise|boost|strengthen|decrease|reduce|lower|weaken) (?:the )?(.+?) (?:strength|amount|intensity|size)(?: by (\d+(?:\.\d+)?) ?%)?$/))) {
    const up = /^(?:increase|raise|boost|strengthen)$/.test(m[1]!);
    const pct = m[3] ? Number(m[3]) / 100 : 0.5;
    if (!up && pct >= 1) return err(segment, 'A reduction must be less than 100%.', 'reduce the pulse strength by 50%');
    return { command: { type: 'sync.strength', which: m[2]!.trim(), factor: up ? 1 + pct : 1 - pct } };
  }
  if ((m = segment.match(/^make (?:the )?(.+?) (stronger|bigger|weaker|smaller|subtler)$/)) && /pulse|bounce|flash|shake|spin|glow|fade|reaction|selected/.test(m[1]!)) {
    return { command: { type: 'sync.strength', which: m[1]!.trim(), factor: /stronger|bigger/.test(m[2]!) ? 1.5 : 1 / 1.5 } };
  }

  // ---- Remove a reaction.
  if ((m = segment.match(/^(?:remove|delete|drop|clear) (?:the )?(?:(.+?) )?(reactions?|mappings?|pulses?|bounces?|flash(?:es)?|shakes?|spins?|glows?|fades?|cues?|motion cues?)$/))) {
    const words = (m[1] ?? '').trim();
    const category = words && words !== 'selected' ? eventCategory(words) : null;
    if (words && words !== 'selected' && !category) return null;
    return { command: { type: 'sync.remove', which: words === 'selected' ? 'selected' : m[2]!.replace(/s$|es$/, ''), category } };
  }

  // ---- Markers.
  if ((m = segment.match(new RegExp(String.raw`^(?:add|place|put|drop) (?:a |an )?(?:manual )?(?:marker|event|cue point) at ${POSITION}(?: (?:called|named|labell?ed) (.+))?$`)))) {
    const tick = parsePosition(m[1]!);
    if (tick === null) return err(segment, 'Use a position like “bar 5”, “bar 5 beat 3” or “beat 16”.', 'add a marker at bar 5 called drop');
    return { command: { type: 'sync.marker', tick, label: (m[m.length - 1] ?? '').trim().slice(0, 40) } };
  }

  // ---- Move a cue or marker.
  if ((m = segment.match(new RegExp(String.raw`^move (?:the )?(.+?) (?:cue |marker )?to ${POSITION}$`)))) {
    const tick = parsePosition(m[2]!);
    if (tick === null) return err(segment, 'Use a position like “bar 5”, “bar 5 beat 3” or “beat 16”.', 'move the jump to beat 16');
    return { command: { type: 'sync.move', name: m[1]!.replace(/ (?:cue|marker)$/, '').trim(), tick } };
  }

  // ---- Motion cues: "start the running animation at bar 3", "jump at bar 9".
  if ((m = segment.match(new RegExp(String.raw`^start (?:the )?([a-z]+)(?: animation| motion| cycle)? at ${POSITION}$`)))) {
    const template = NOUNS[m[1]!] ?? verbAt(m[1]!)?.template;
    const tick = parsePosition(m[2]!);
    if (!template || tick === null) return null;
    return { command: { type: 'sync.cue', template, target: { kind: 'inherit' }, tick, everyTicks: null } };
  }

  // ---- "make <target> <verb> ..." (reaction on events, or motion every N bars). The target
  // may be several words, so every split point is tried (first that parses wins).
  if ((m = segment.match(/^(?:make|have|let) (.+)$/))) {
    const words = m[1]!.split(' ');
    for (let i = 1; i < words.length - 1; i++) {
      const target = syncTarget(words.slice(0, i).join(' '));
      if (!target) continue;
      const rest = words.slice(i).join(' ');
      const verb = /^lights? up /.test(rest) ? rest.match(/^lights? up/)![0] : words[i]!;
      const after = rest.slice(verb.length).trim();
      const reacted = reactionOf(verb);
      const events = parseEvents(after);
      if (reacted && events) {
        if (target.kind === 'scene') return err(segment, `“${verb}” acts on an object; for the whole scene use flash or shake.`, 'flash the background on every kick');
        return { command: { type: 'sync.react', reaction: reacted.reaction, categories: events.categories, every: events.every, target, ...(reacted.direction ? { direction: reacted.direction } : {}) } };
      }
      const motion = verbAt(rest);
      if (motion) {
        const cue = cueTiming(motion.rest);
        if (cue === 'events') return err(segment, 'Motions are placed on bars or beats (“every four bars”, “at bar 3”). For reactions to events use pulse, bounce, spin, fade, glow or blink.', 'make the character jump every four bars');
        if (cue) return { command: { type: 'sync.cue', template: motion.template, target, tick: cue.tick, everyTicks: cue.everyTicks } };
      }
    }
  }

  // ---- "<verb> <target> on every <event>" (imperative).
  if ((m = segment.match(/^(flash|shake|pulse|bounce|spin|rotate|fade|dim|glow|light up|blink|toggle|flicker|wobble) (?:the )?(.+?) (on .+)$/))) {
    const events = parseEvents(m[3]!);
    if (!events) return null;
    const target = syncTarget(m[2]!);
    if (!target) return null;
    let reaction: ReactionType;
    if (m[1] === 'flash' && target.kind === 'scene') reaction = /background|bg/.test(m[2]!) ? 'background' : 'flash';
    else if (m[1] === 'shake') {
      if (target.kind !== 'scene') return err(segment, 'Shake applies to the camera: “shake the camera on every 808”.', 'shake the camera on every 808');
      reaction = 'shake';
    } else {
      const r = reactionOf(m[1]!);
      if (!r) return null;
      if (target.kind === 'scene') return err(segment, `“${m[1]}” acts on an object; for the whole scene use flash or shake.`, 'flash the background on every kick');
      return { command: { type: 'sync.react', reaction: r.reaction, categories: events.categories, every: events.every, target, ...(r.direction ? { direction: r.direction } : {}) } };
    }
    return { command: { type: 'sync.react', reaction, categories: events.categories, every: events.every, target } };
  }
  return null;
}

/** "every four bars [starting at bar 2]", "at bar 3", "on beat 9"; 'events' for "on every kick". */
function cueTiming(rest: string): { tick: number; everyTicks: number | null } | 'events' | null {
  const r = rest.trim();
  if (/^on (?:every|each) /.test(r) && parseEvents(r)) return 'events';
  let m = r.match(new RegExp(String.raw`^(?:once )?every (?:${NUMBER} )?(bars?|beats?)(?: (?:starting|from|beginning) (?:at|on|from) ${POSITION})?$`));
  if (m) {
    const n = m[1] ? toNumber(m[1]) : 1;
    if (!Number.isInteger(n) || n < 1 || n > 64) return null;
    const unit = /^bar/.test(m[2]!) ? TICKS_PER_BAR : TICKS_PER_BEAT;
    const tick = m[3] ? parsePosition(m[3]) : 0;
    return tick === null ? null : { tick, everyTicks: n * unit };
  }
  m = r.match(new RegExp(String.raw`^(?:at|on|from) ${POSITION}$`));
  if (m) {
    const tick = parsePosition(m[1]!);
    return tick === null ? null : { tick, everyTicks: null };
  }
  return null;
}

export const isSyncCommand = (c: Command): boolean => c.type.startsWith('sync.');
